import {Stringifier} from '../../services/stringifier.service';
import {MediaSource} from '../media-source';
import {Type} from 'class-transformer';
import {addDays, addMonths} from 'date-fns';

@Stringifier
export class Movie {
  movieId: number = 0;
  key: string = '';
  year: number = new Date().getFullYear();
  direction: string = '';
  cast: string = '';
  @Type(() => Date)
  ltr?: Date;
  boxOffice?: number;
  @Type(() => Date)
  firstTxSky?: Date;
  note: string = '';

  @Type(() => Date)
  createdAt: Date = new Date();
  @Type(() => Date)
  updatedAt?: Date;

  published: boolean = false;
  @Type(() => Date)
  publishedAt?: Date = new Date();

  status: MovieStatus = MovieStatus.Draft;
  special8M: boolean = false;

  sliderSequence?: number;

  @Type(() => MovieGenreAssociations)
  movieGenreAssociations: MovieGenreAssociations[] = [];

  @Type(() => MovieAvailability)
  movieAvailability: MovieAvailability[] = [
    new MovieAvailability(Window.Pay1, 1),
    new MovieAvailability(Window.Pay2, 2),
    new MovieAvailability(Window.Pay3, 3),
  ];

  @Type(() => MovieDetails)
  movieDetails: MovieDetails[] = [
    new MovieDetails(Language.Italian),
    new MovieDetails(Language.English),
  ];

  showAvailability = false;
  calculationMode: MovieCalculationMode = MovieCalculationMode.FreeFields;

  otherProducers: string = '';
  productionBudget = '';
  tentative: Tentative = Tentative.NoValue;
  skyMovieRights: SkyMovieRights = SkyMovieRights.Exclusive;
  skyCoExclusiveSoldStatus: SkyCoExclusiveSoldStatus = SkyCoExclusiveSoldStatus.ToBeSold;

  get itaDetails(): MovieDetails {
    return this.movieDetails.find(x => x.language === Language.Italian)!;
  }

  get title() {
    return this.itaDetails?.title || this.engDetails?.title;
  }

  get sliderImageFullPath(): string {
    return this.itaDetails?.sliderImage?.fullPath || this.engDetails?.sliderImage?.fullPath || '';
  }

  get engDetails(): MovieDetails {
    return this.movieDetails.find(x => x.language === Language.English)!;
  }

  applyAutomations() {
    if (this.calculationMode !== MovieCalculationMode.AutoFormulaV01) {
      return;
    }

    this.movieAvailability = this.movieAvailability.sort(x => x.sequence);

    if (this.ltr) {
      const pay1 = this.movieAvailability.find(x => x.window === Window.Pay1)!;
      const pay2 = this.movieAvailability.find(x => x.window === Window.Pay2)!;
      const pay3 = this.movieAvailability.find(x => x.window === Window.Pay3)!;

      // pay1.from è ltr + 105 giorni
      pay1.from = addDays(this.ltr, 105);
      let fromDate = pay1.from;
      if (this.firstTxSky) {
        if (pay1.movieRights === MovieRights.CoExclusive) {
          // data massima tra firstTxSky e pay1.from
          fromDate = this.firstTxSky > pay1.from ? this.firstTxSky : pay1.from;
        } else {
          // data minima tra firstTxSky e pay1.from
          fromDate = this.firstTxSky < pay1.from ? this.firstTxSky : pay1.from;
        }
      }
      pay1.months = 12;
      pay1.expiration = addDays(fromDate, 364);

      // pay2.from è pay1.expiration + 1 giorni
      pay2.from = addDays(pay1.expiration, 1);
      if (this.special8M) {
        pay2.movieRights = MovieRights.CoExclusive;
      } else {
        pay2.movieRights = MovieRights.Exclusive;
      }
      pay2.months = this.calculateMonths(pay2.movieRights);
      pay2.expiration = this.calculateExpirationFromMonths(pay2.from, pay2.months);

      pay3.from = addDays(pay2.expiration, 1);

      // foreach in sequence of movieAvailability
      let previousAvailability: MovieAvailability = pay2;
      this.movieAvailability.forEach((movieAvailability, index) => {
        if (index === 0 || index === 1) {
          return; // skip pay1, pay2
        }
        // movieAvailability.from ??= addDays(previousAvailability.expiration!, 1);
        // movieAvailability.months = !!movieAvailability.months ? movieAvailability.months : 0;
        if (movieAvailability.months > 0 && movieAvailability.from) {
          movieAvailability.expiration = this.calculateExpirationFromMonths(movieAvailability.from, movieAvailability.months);
        } else {
          movieAvailability.expiration = undefined;
        }
        previousAvailability = movieAvailability;
      });
    }
  }

  private calculateMonths(movieRights: MovieRights) {
    return movieRights === MovieRights.Exclusive ? 6 : 12;
  }

  private calculateExpirationFromMonths(from: Date, months: number): Date {
    return addDays(addMonths(from, months), -1);
  }

  static getStringifier(level: number): Movie | null {
    if (level < 0) {
      return null;
    }
    // @ts-ignore
    return {
      movieId: null,
      key: null,
      year: null,
      direction: null,
      cast: null,
      ltr: null,
      boxOffice: null,
      firstTxSky: null,
      note: null,
      createdAt: null,
      updatedAt: null,
      published: null,
      publishedAt: null,
      status: null,
      special8M: null,
      movieGenreAssociations: null,
      movieAvailability: null,
      movieDetails: null,
      showAvailability: null,
      calculationMode: null,
    } as Movie;
  }
}

export class MovieDetails {
  movieDetailsId: number = 0;
  movieId: number = 0;
  language: Language = Language.Italian;
  coverImageId?: number;
  @Type(() => MediaSource)
  coverImage?: MediaSource;
  sliderImageId?: number;
  @Type(() => MediaSource)
  sliderImage?: MediaSource;
  title: string = '';
  production: string = '';
  info: string = '';
  linkImdb: string = '';
  linkTrailer: string = '';
  awards: string = '';
  synopsis: string = '';
  @Type(() => Date)
  createdAt: Date = new Date();
  @Type(() => Date)
  updatedAt?: Date = new Date();

  constructor(language: Language = Language.Italian) {
    this.language = language;
  }
}

export class MovieAvailability {
  movieAvailabilityId: number = 0;
  movieId: number = 0;
  movie?: Movie;
  sequence: number = 0;
  @Type(() => Date)
  from?: Date;
  @Type(() => Date)
  expiration?: Date;
  months: number = 0;
  customerName: string = '';
  movieRights: MovieRights | null = MovieRights.Exclusive;
  tentative: Tentative = Tentative.NoValue;
  status: MovieAvailabilityStatus | null = MovieAvailabilityStatus.Current;
  window: Window = Window.Pay1;
  note: string = '';
  freeTvLicensePeriod: string = '';
  svodCurrentPreviousPlatform: string = '';
  svodLibraryTbc: string = '';
  freeTvTentativ: string = '';

  @Type(() => Date)
  createdAt: Date = new Date();
  @Type(() => Date)
  updatedAt?: Date = new Date();

  constructor(window: Window = Window.Pay1, sequence = 0) {
    this.window = window;
    this.sequence = sequence;
  }

  getMonthsAvail() {
    // differenza tra Start date e End date in mesi
    if (this.from && this.expiration) {
      if (this.from.getTime() === this.expiration.getTime()) return 0;
      const start = this.from;
      const end = this.expiration;
      let months = (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth());
      if (end.getDate() >= start.getDate()) months += 1;
      return Math.max(0, months);

    }
    return 0;
  }
}

export class MovieGenreAssociations {
  movieGenreAssociationId: number = 0;
  movieId: number = 0;
  movieGenreId: number = 0;
  @Type(() => MovieGenreAssociations)
  movieGenre?: MovieGenre;
  @Type(() => Date)
  createdAt: Date = new Date();
  @Type(() => Date)
  updatedAt?: Date = new Date();

  static create(movieId: number, movieGenreId: number): MovieGenreAssociations {
    const association = new MovieGenreAssociations();
    association.movieId = movieId;
    association.movieGenreId = movieGenreId;
    return association;
  }
}

export class MovieGenre {
  movieGenreId: number = 0;
  name: string = '';
  englishName: string = '';
  isSubGenre: boolean = false;
  @Type(() => Date)
  createdAt: Date = new Date();
  @Type(() => Date)
  updatedAt?: Date = new Date();
}

export enum Language {
  Italian = 'Italian',
  English = 'English',
}

export enum MovieRights {
  Exclusive = 'Exclusive',
  CoExclusive = 'CoExclusive',
  NotExclusive = 'NotExclusive',
}

export enum MovieAvailabilityStatus {
  Current = 'Current',
  Library = 'Library',
  ReRun = 'ReRun',
}

export enum Window {
  Pay1 = 'Pay1',
  Pay2 = 'Pay2',
  Pay3 = 'Pay3',
  Svod = 'Svod',
  FreeTv = 'FreeTv',
  LibrarySvod = 'LibrarySvod',
  LibraryFreeTv = 'LibraryFreeTv',
}

export enum MovieStatus {
  Draft = 'Draft',
  Published = 'Published',
}

export enum MovieCalculationMode {
  Unknown = 'Unknown',
  FreeFields = 'FreeFields',
  AutoFormulaV01 = 'AutoFormulaV01',
}


export enum Tentative {
  NoValue = 'NoValue',
  T = 'T',
  C = 'C',
}

export enum SkyMovieRights {
  Exclusive = 'Exclusive',
  CoExclusive = 'CoExclusive'
}

export enum SkyCoExclusiveSoldStatus {
  Sold = 'Sold',
  ToBeSold = 'ToBeSold',
}
